<!-- eslint-disable vue/no-parsing-error -->
<template>
  <div class="custom-datepicker" ref="datepicker">
    <input
      ref="input"
      type="text"
      :value="formattedDate"
      :placeholder="placeholder"
      @click="openCalendar"
      readonly
      class="datepicker-input"
    />
    <div v-if="isOpen" ref="dropdown" class="calendar-dropdown" :style="dropdownPosition">
      <!-- Header avec navigation -->
      <div class="calendar-header">
        <button type="button" class="nav-button" @click="prevMonth" aria-label="Mois précédent">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" data-sentry-element="ChevronLeft" data-sentry-source-file="index.tsx"><path d="m15 18-6-6 6-6"></path></svg>
        </button>
        <div class="month-year-selects">
          <span class="select-wrapper">
            <select v-model.number="currentMonth" class="month-select" @change="onMonthChange">
              <option v-for="(month, index) in monthOptions" :key="index" :value="index">
                {{ month }}
              </option>
            </select>
            <span class="select-label" aria-hidden="true">
              {{ monthOptions[currentMonth] }}
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chevron-icon">
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </span>
          </span>
          <span class="select-wrapper">
            <select v-model.number="currentYear" class="year-select" @change="onYearChange">
              <option v-for="year in yearOptions" :key="year" :value="year">
                {{ year }}
              </option>
            </select>
            <span class="select-label" aria-hidden="true">
              {{ currentYear }}
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chevron-icon">
                <path d="m6 9 6 6 6-6"></path>
              </svg>
            </span>
          </span>
        </div>
        <button type="button" class="nav-button" @click="nextMonth" aria-label="Mois suivant">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"  aria-hidden="true" data-sentry-element="ChevronRight" data-sentry-source-file="index.tsx"><path d="m9 18 6-6-6-6"></path></svg>
        </button>
      </div>

      <!-- Jours de la semaine -->
      <div class="weekdays">
        <span v-for="(day, index) in weekDays" :key="index" class="weekday">{{ day }}</span>
      </div>

      <!-- Grille de dates -->
      <div class="days-grid">
        <button
          v-for="day in visibleDays"
          :key="day.id"
          type="button"
          :class="{
            'day-button': true,
            'is-today': day.isToday,
            'is-selected': day.isSelected,
            'is-selectable': day.isSelectable && day.isCurrentMonth,
            'is-current-month': day.isCurrentMonth,
            'is-other-month': !day.isCurrentMonth
          }"
          :disabled="!day.isSelectable || !day.isCurrentMonth"
          @click="selectDate(day)"
        >
          <span :class="{ 'invisible-text': !day.isCurrentMonth }">
            {{ day.day }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DatePicker',
  props: {
    value: {
      type: Date,
      default: null
    },
    minDate: {
      type: Date,
      default: null
    },
    maxDate: {
      type: Date,
      default: null
    },
    placeholder: {
      type: String,
      default: ''
    },
    focusedDate: {
      type: Date,
      default: null
    },
    align: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right'].includes(value)
    }
  },
  data() {
    return {
      isOpen: false,
      currentMonth: new Date().getMonth(),
      currentYear: new Date().getFullYear(),
      dropdownPosition: {}
    };
  },
  computed: {
    formattedDate() {
      if (!this.value) return '';
      const locale = this.$i18n ? this.$i18n.locale : 'fr-FR';
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(this.value);
    },
    monthOptions() {
      const locale = this.$i18n ? this.$i18n.locale : 'fr-FR';
      const months = [];
      for (let i = 0; i < 12; i++) {
        const date = new Date(2026, i, 1);
        let monthName = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
        // Retirer les points et autres caractères de ponctuation
        monthName = monthName.replace(/\./g, '');
        months.push(monthName);
      }
      return months;
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      // Générer une plage d'années (par exemple, 10 ans avant et 10 ans après)
      for (let i = currentYear - 10; i <= currentYear + 10; i++) {
        years.push(i);
      }
      return years;
    },
    weekDays() {
      const locale = this.$i18n ? this.$i18n.locale : 'fr-FR';
      const weekDays = [];
      // Créer un tableau des jours de la semaine en commençant par lundi
      for (let i = 0; i < 7; i++) {
        // En commençant par lundi (5 janvier 2026 est un lundi)
        const date = new Date(2026, 0, 5 + i);
        const dayName = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
        // Prendre seulement les 2 premières lettres
        weekDays.push(dayName.substring(0, 2));
      }
      return weekDays;
    },
    calendarDays() {
      const days = [];
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);

      // Calculer le premier jour à afficher (lundi de la semaine contenant le 1er)
      let startDay = firstDay.getDay();
      startDay = startDay === 0 ? 6 : startDay - 1; // Convertir dimanche=0 à dimanche=6

      // Jours du mois précédent
      const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate();
      for (let i = startDay - 1; i >= 0; i--) {
        const day = prevMonthLastDay - i;
        const date = new Date(this.currentYear, this.currentMonth - 1, day);
        days.push({
          id: `prev-${day}`,
          day: day,
          date: date,
          isCurrentMonth: false,
          isToday: this.isToday(date),
          isSelected: this.isSelected(date),
          isSelectable: this.isDateSelectable(date)
        });
      }

      // Jours du mois actuel
      for (let day = 1; day <= lastDay.getDate(); day++) {
        const date = new Date(this.currentYear, this.currentMonth, day);
        days.push({
          id: `current-${day}`,
          day: day,
          date: date,
          isCurrentMonth: true,
          isToday: this.isToday(date),
          isSelected: this.isSelected(date),
          isSelectable: this.isDateSelectable(date)
        });
      }

      // Jours du mois suivant pour compléter la grille
      const remainingDays = 42 - days.length; // 6 semaines * 7 jours
      for (let day = 1; day <= remainingDays; day++) {
        const date = new Date(this.currentYear, this.currentMonth + 1, day);
        days.push({
          id: `next-${day}`,
          day: day,
          date: date,
          isCurrentMonth: false,
          isToday: this.isToday(date),
          isSelected: this.isSelected(date),
          isSelectable: this.isDateSelectable(date)
        });
      }

      return days;
    },
    visibleDays() {
      // Grouper les jours par semaine
      const weeks = [];
      for (let i = 0; i < this.calendarDays.length; i += 7) {
        weeks.push(this.calendarDays.slice(i, i + 7));
      }

      // Filtrer les semaines qui ne contiennent aucun jour du mois courant
      // On affiche toutes les semaines qui ont au moins 1 jour du mois courant
      const visibleWeeks = weeks.filter(week => {
        return week.some(day => day.isCurrentMonth);
      });

      // Aplatir le tableau
      return visibleWeeks.flat();
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.currentMonth = newVal.getMonth();
        this.currentYear = newVal.getFullYear();
      }
    },
    focusedDate(newVal) {
      if (newVal && !this.value) {
        this.currentMonth = newVal.getMonth();
        this.currentYear = newVal.getFullYear();
      }
    }
  },
  mounted() {
    // Initialiser le mois/année en fonction de la valeur, focusedDate ou de la date min
    if (this.value) {
      this.currentMonth = this.value.getMonth();
      this.currentYear = this.value.getFullYear();
    } else if (this.focusedDate) {
      this.currentMonth = this.focusedDate.getMonth();
      this.currentYear = this.focusedDate.getFullYear();
    } else if (this.minDate) {
      this.currentMonth = this.minDate.getMonth();
      this.currentYear = this.minDate.getFullYear();
    }
  },
  methods: {
    toggleCalendar() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.updateDisplayedMonth();
        this.addClickOutsideListener();
      } else {
        this.removeClickOutsideListener();
      }
    },
    openCalendar() {
      if (!this.isOpen) {
        this.isOpen = true;
        this.updateDisplayedMonth();
        this.dropdownPosition = {}; // Reset position to allow natural measurement
        // Calculer la position du dropdown
        this.$nextTick(() => {
          this.calculateDropdownPosition();
          this.addClickOutsideListener();
        });
      }
    },
    calculateDropdownPosition() {
      const input = this.$refs.input;
      const dropdown = this.$refs.dropdown;
      if (!input || !dropdown) return;

      // Temporarily remove max-height to measure natural height
      const prevMaxHeight = dropdown.style.maxHeight;
      dropdown.style.maxHeight = 'none';
      const dropdownHeight = dropdown.offsetHeight;
      const dropdownWidth = dropdown.offsetWidth;
      dropdown.style.maxHeight = prevMaxHeight;

      const rect = input.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      let position = {};

      // Vertical positioning: Prefer bottom, but switch to top if not enough space below
      // and there's more space above.
      let finalMaxHeight = 0;
      if (spaceBelow < (dropdownHeight + 10) && spaceAbove > spaceBelow) {
        position.bottom = `${viewportHeight - rect.top + 4}px`;
        finalMaxHeight = spaceAbove - 20;
      } else {
        position.top = `${rect.bottom + 4}px`;
        finalMaxHeight = spaceBelow - 20;
      }

      // Horizontal positioning: Clamp to viewport with 10px margin
      const margin = 10;
      let left = rect.left;
      
      if (this.align === 'right') {
        left = rect.right - dropdownWidth;
      }

      // If the calculated left + width would overflow the right, shift it left
      if (left + dropdownWidth > viewportWidth - margin) {
        left = viewportWidth - dropdownWidth - margin;
      }

      // If the resulting left is too far left, clamp it to the margin
      if (left < margin) {
        left = margin;
      }

      position.left = `${left}px`;
      // Ensure the dropdown doesn't exceed the available viewport width
      position.maxWidth = `calc(100vw - 20px)`;

      // Force a max-height and overflow if the dropdown is still taller than available space
      position.maxHeight = `${Math.max(160, finalMaxHeight)}px`;
      position.overflowY = 'auto';
      position.overflowX = 'hidden';

      this.dropdownPosition = position;
    },
    updateDisplayedMonth() {
      // Mettre à jour le mois affiché quand on ouvre le calendrier
      if (this.value) {
        this.currentMonth = this.value.getMonth();
        this.currentYear = this.value.getFullYear();
      } else if (this.focusedDate) {
        this.currentMonth = this.focusedDate.getMonth();
        this.currentYear = this.focusedDate.getFullYear();
      } else if (this.minDate) {
        this.currentMonth = this.minDate.getMonth();
        this.currentYear = this.minDate.getFullYear();
      }
    },
    closeCalendar() {
      this.isOpen = false;
      this.removeClickOutsideListener();
    },
    handleScroll(event) {
      if (this.$refs.dropdown && this.$refs.dropdown.contains(event.target)) {
        return;
      }
      this.closeCalendar();
    },
    handleClickOutside(event) {
      if (this.$refs.datepicker && !this.$refs.datepicker.contains(event.target)) {
        this.closeCalendar();
      }
    },
    handleResize() {
      // Recalculer la position au lieu de fermer
      this.calculateDropdownPosition();
    },
    addClickOutsideListener() {
      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('touchstart', this.handleClickOutside);
      window.addEventListener('scroll', this.handleScroll, true);
      window.addEventListener('resize', this.handleResize);
    },
    removeClickOutsideListener() {
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('touchstart', this.handleClickOutside);
      window.removeEventListener('scroll', this.handleScroll, true);
      window.removeEventListener('resize', this.handleResize);
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
    },
    onMonthChange() {
      // Le v-model met déjà à jour currentMonth
      // Cette méthode peut être utilisée pour des actions supplémentaires si nécessaire
    },
    onYearChange() {
      // Le v-model met déjà à jour currentYear
      // Cette méthode peut être utilisée pour des actions supplémentaires si nécessaire
    },
    selectDate(day) {
      if (day.isSelectable) {
        this.$emit('input', day.date);
        this.closeCalendar();
      }
    },
    isDateSelectable(date) {
      // Créer des dates à minuit pour la comparaison
      const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      if (this.minDate) {
        const minDateMidnight = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate());
        if (checkDate < minDateMidnight) return false;
      }

      if (this.maxDate) {
        const maxDateMidnight = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate());
        if (checkDate > maxDateMidnight) return false;
      }

      return true;
    },
    isToday(date) {
      const today = new Date();
      return date.getDate() === today.getDate() &&
             date.getMonth() === today.getMonth() &&
             date.getFullYear() === today.getFullYear();
    },
    isSelected(date) {
      if (!this.value) return false;
      return date.getDate() === this.value.getDate() &&
             date.getMonth() === this.value.getMonth() &&
             date.getFullYear() === this.value.getFullYear();
    }
  },
  beforeDestroy() {
    // Nettoyer le listener quand le composant est détruit
    this.removeClickOutsideListener();
  }
};
</script>

<style scoped>
.custom-datepicker {
  position: relative;
  font-family: var(--tally-font-family);
  width: 100%;
  overflow: visible;
}

.datepicker-input {
  width: 100%;
  height: 36px;
  padding: 0 10px;
  font-family: var(--tally-font-family);
  font-size: var(--tally-text-sm);
  color: var(--tally-text-primary);
  background-color: var(--tally-white);
  border: 0;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 1px 0px,
              rgba(61, 59, 53, 0.16) 0px 0px 0px 1px,
              rgba(61, 59, 53, 0.08) 0px 2px 5px 0px;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}

.datepicker-input::placeholder {
  color: var(--tally-placeholder);
  text-transform: lowercase;
}

.datepicker-input:hover {
  box-shadow: rgba(0, 0, 0, 0.12) 0px 1px 1px 0px,
              rgba(61, 59, 53, 0.16) 0px 0px 0px 1px,
              rgba(61, 59, 53, 0.08) 0px 3px 9px 0px,
              rgba(61, 59, 53, 0.08) 0px 2px 5px 0px;
}

.datepicker-input:focus {
  outline: none;
  box-shadow: rgba(58, 152, 255, 0.36) 0px 0px 0px 4px,
              rgba(61, 59, 53, 0.16) 0px 0px 0px 1px;
}

.calendar-dropdown {
  position: fixed;
  background: var(--tally-white);
  border: 1px solid var(--tally-border);
  border-radius: var(--tally-radius-md);
  box-shadow: rgba(0, 0, 0, 0.09) 0px 3px 12px;
  padding: 6px;
  z-index: 9999;
  min-width: 280px;
  max-width: calc(100vw - 20px);
  box-sizing: border-box;
}

@media (max-width: 320px) {
  .calendar-dropdown {
    min-width: auto;
    width: calc(100vw - 20px);
  }
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.month-year-selects {
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  justify-content: center;
}

.select-wrapper {
  position: relative;
  display: inline-block;
}

.month-select,
.year-select {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
}

.select-label {
  display: inline-flex;
  align-items: baseline;
  gap: 0;
  font-size: 18px;
  font-weight: 500;
  color: rgb(55, 53, 47);
  font-family: var(--tally-font-family);
  pointer-events: none;
  padding: 4px 0;
}

.chevron-icon {
  width: 16px;
  height: 16px;
  color: rgb(55, 53, 47);
  margin-left: 0;
}

.month-select:focus,
.year-select:focus {
  outline: none;
}

.nav-button {
  background-color: var(--tally-sidebar-hover);
  border: none;
  cursor: pointer;
  padding: 8px;
  color: var(--tally-text-secondary);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.nav-button:hover {
  background-color: #e0e0e0;
  color: var(--tally-text-primary);
}

.nav-button:active {
  transform: scale(0.95);
}

.month-year {
  font-size: var(--tally-text-base);
  font-weight: var(--tally-font-semibold);
  color: var(--tally-text-primary);
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.weekday {
  text-align: center;
  font-size: 10px;
  font-weight: 400;
  color: rgb(137, 136, 132);
  padding: 4px 0;
}

.days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.day-button {
  width: 100%;
  aspect-ratio: 1/1;
  max-width: 40px;
  max-height: 40px;
  color: rgb(119, 118, 114);
  font-size: var(--tally-text-base);
  border: none;
  background: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0 auto;
}

.day-button.is-other-month {
  color: var(--tally-text-muted);
}

.day-button.is-selectable:hover {
  background-color: rgba(0, 116, 212, 0.1);
  color: var(--tally-primary);
}

.day-button.is-selectable:active {
  transform: scale(0.95);
}

.day-button.is-selected {
  background-color: var(--tally-primary);
  color: var(--tally-white);
  font-weight: var(--tally-font-semibold);
}

.day-button.is-today:not(.is-selected) {
  color: var(--tally-primary);
  font-weight: var(--tally-font-semibold);
}

.day-button:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.invisible-text {
  color: transparent !important;
  user-select: none;
}

</style>
